import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const API_BASE_URL = process.env.REACT_APP_API_URL;

const STORAGE_KEY = "skillskulpt_user";

const AuthContext = createContext(null);

function readStoredUser() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => readStoredUser());

  const updateUser = useCallback((updater) => {
    setUser((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (next) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
      return next;
    });
  }, []);

  const signIn = useCallback((nextUser) => {
    updateUser(nextUser);
  }, [updateUser]);

  const signOut = useCallback(() => {
    updateUser(null);
  }, [updateUser]);

  useEffect(() => {
    if (!user?.idToken || !API_BASE_URL) return;

    // Refresh before token expires in 1 hour (every 50 minutes)
    const REFRESH_INTERVAL_MS = 50 * 60 * 1000;

    const refresh = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.idToken}`,
          },
          body: user.refreshToken
            ? JSON.stringify({ refreshToken: user.refreshToken })
            : undefined,
        });

        if (!response.ok) {
          // If refresh fails, clear auth state and redirect to login page
          updateUser(null);
          window.location.href = '/';
          return;
        }

        const data = await response.json().catch(() => ({}));
        const nextIdToken = data.idToken || data.token || data.accessToken;
        const nextRefreshToken = data.refreshToken || user.refreshToken;

        if (!nextIdToken) return;

        updateUser((prev) =>
          prev
            ? {
                ...prev,
                idToken: nextIdToken,
                refreshToken: nextRefreshToken,
              }
            : prev,
        );
      } catch {
        // Swallow errors; background refresh should be silent.
      }
    };

    const intervalId = setInterval(refresh, REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [user?.idToken, user?.refreshToken, updateUser]);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      signIn,
      signOut,
    }),
    [user, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}

